/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, EventEmitter, Output, ViewChild } from "@angular/core";
import { PropertyDataUI } from "app/components/infrastructure/dialogs/property-data-ui";
import { DraggableDirective } from "app/directives/draggable.directive";
import { InstanceData } from "app/models/instance-data";
import { ObjectCategory } from "app/models/object-category";
import { ObjectInstance } from "app/models/object-instance";
import { PropertyTypeUtil } from "app/models/property-type";
import { ObjectService } from "app/services/object.service";

declare var $: any;

/**
 * Popup Dialog used for the "Edit Data" Action on the Locate Page for Assets.
 */
@Component({
    selector: 'ef-instance-data-dialog',
    templateUrl: './instance-data-dialog.component.html'
})
export class InstanceDataDialogComponent {

    // Draggable Directive added to that the popup is draggable.  Referenced so that the position can be reset.
    @ViewChild(DraggableDirective) draggableDirective: DraggableDirective;

    // Output used to throw an Event with the updated Instance information after successfully updating the data
    @Output()
    updateCompletedSuccessfullyEvent = new EventEmitter<ObjectInstance>();

    // Reference to the Instance whose data is being updated
    instance: ObjectInstance = null;

    /** Property and the Data defined for it.  Instance's Data is parsed into these objects which are displayed in the UI and parsed back upon saving */
    propertyDataUis: PropertyDataUI[] = [];


    constructor(
        private objectService: ObjectService
    ) { }


    /**
     * Called to set the Asset whose Data is being displayed / edited / saved by this AssetDataDialog.
     */
    public setInstance(instance: ObjectInstance): void {
        console.log(`Setting Instance: ${JSON.stringify(instance)}`);
        // Set the Asset which is being updated
        this.instance = instance;

        this.parseInstanceData();
    }

    /**
     * Parses the current Instance and updates the "propertyDataUis" with the 
     * information from the Property / Data values. 
     */
    private parseInstanceData(): void {
        while (this.propertyDataUis.length > 0) {
            this.propertyDataUis.pop();
        }

        if (this.instance?.data) {
            // Copy the Data from the Instance and use this to generate the PropertyDataUis as that will require adjusting the "object_value" values
            const copiedOriginalData = JSON.parse(JSON.stringify(this.instance.data));

            // Cycle through all the properties for the Instance
            for (const property of this.instance?.object?.properties) {
                // Create a PropertyDataUI that will hold the property and any Data defined for it
                const propertyDataUi = new PropertyDataUI();
                propertyDataUi.mProperty = property;
                propertyDataUi.mData = [];

                // Cycle through the copied data, adding any for the Property to the PropertyDataUi (after adjusting the "object_value" if needed)
                for (const datum of copiedOriginalData) {
                    if (datum?.property?.id == property?.id) {
                        datum.property = property;
                        // Fix the "object_data" value of the incoming "InstanceData" as the JSON is structured weird coming from the backend.
                        PropertyTypeUtil.fixIncomingInstanceDataObjectValueForType(property.type, datum);
                        propertyDataUi.mData.push(datum);
                    }
                }

                // If no Data was defined for the Property, add one so the User can add one if they want.
                if (propertyDataUi.mData.length == 0) {
                    const newDatum = new InstanceData();
                    newDatum.property = property;
                    propertyDataUi.mData.push(newDatum);
                }

                // Add the PropertyDataUI to the List to be displayed / edited
                this.propertyDataUis.push(propertyDataUi);
            }
        }
    }

    /**
     * Called by the UI to obtain the Title to use for the AssetDataDialog
     */
    public getDialogTitle(): string {
        switch (this.instance?.object?.category) {
            case ObjectCategory.PEOPLE: 
                return `Save Data for Person: '${this.instance?.name}'?`
            case ObjectCategory.ASSET: 
                return `Save Data for Asset: '${this.instance?.name}'?`
            default: 
                return `Save Data for Unknown Instance Type? [${this.instance?.object?.category}] - ${this.instance?.name}`;
        }
    }

    /**
     * Called by the UI to show this AssetDataDialog.
     * 
     * This also resets the "Drag" position so that it appears in the same position
     * every time it is opened. 
     */
    public show(): void {
        this.draggableDirective.reset(); // Reset the Draggable Directive so that the Dialog pops up in the same place (Horizontally centered at the top of the screen)
        $("#instanceDataDialog").show();
    }

    /**
     * Called to hide the AssetDataDialog
     */
    public hide(): void {
        $("#instanceDataDialog").hide();
    }

    /**
     * Called by the UI to update the Instance Data for the Asset.
     * 
     * This calls the REST API, then emits a "Success" Event with the updated Asset information.
     */
    public async updateData(): Promise<void> {
        // Create an Array of the Data elements that aren't marked as readonly
        const updateData: InstanceData[] = [];
        {
            const tempData: InstanceData[] = [];
            for (const dataUi of this.propertyDataUis) {
                tempData.push(...dataUi.mData);
            }
            updateData.push(...(JSON.parse(JSON.stringify(tempData)) as InstanceData[]));
            if (updateData.length > 0) {
                updateData.forEach(datum => PropertyTypeUtil.fixOutgoingInstanceDataObjectValueForType(datum.property.type, datum));
            }
        }
        

        // Call the API to update the Data for the Asset... This returns the Asset with up-to-date information...
        let instance: ObjectInstance = null;
        try {
            instance = await this.objectService.updateObjectInstanceData(this.instance.id, updateData);
        } catch (error) {
            console.error("Problem updating Asset's Data.", error);
            return;
        }

        // Sort the Asset's Data so it's in order using the Property Order (seeing this come back in a random order)
        instance.data.sort((a, b) => a.property.order - b.property.order);

        // Emit the up-to-date version of the Asset in a "Success Event"
        this.updateCompletedSuccessfullyEvent.emit(instance);
    }

}
